import { ArrowRight, CalendarDays, Check } from "lucide-react";
import { Link } from "react-router";
import { posthog } from "../../lib/posthog";

type BookDemoProps = {
  location: string;
  product?: "foresight" | "iviz";
  variant?: "nav" | "button" | "card";
  tone?: "default" | "foresight";
  title?: string;
  description?: string;
  className?: string;
};

export function BookDemo({
  location,
  product = "foresight",
  variant = "button",
  tone = "default",
  title = "See Coharts in action.",
  description = "Book a focused 30-minute walkthrough built around your data, workflow, or clinical program.",
  className = "",
}: BookDemoProps) {
  const isForesight = tone === "foresight";
  const contactPath = `/contact/${product}`;
  const track = () => posthog.capture("cta_clicked", { cta: "Book a Demo", location });
  const accent = isForesight
    ? "bg-[#5B21B6] text-white dark:bg-[#7C4DFF] dark:text-white"
    : "bg-[#0A0A0F] text-white dark:bg-white dark:text-[#050505]";

  if (variant === "nav") {
    return (
      <Link
        to={contactPath}
        onClick={track}
        className={`group inline-flex items-center gap-2 rounded-full border border-[#0A0A0F]/12 bg-white/70 px-3.5 py-2 text-sm font-medium text-[#0A0A0F] shadow-sm shadow-black/[0.03] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#1E4FD8]/40 hover:bg-[#1E4FD8]/[0.06] hover:shadow-md active:translate-y-0 active:scale-[0.97] active:bg-[#1E4FD8]/[0.12] active:shadow-inner focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1E4FD8] dark:border-white/12 dark:bg-white/[0.06] dark:text-white dark:hover:border-[#4A90D9]/50 dark:hover:bg-[#4A90D9]/[0.14] dark:active:bg-[#4A90D9]/[0.24] ${className}`}
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#1E4FD8]/10 text-[#1E4FD8] transition-transform duration-200 group-hover:scale-110 group-hover:-rotate-6 group-active:scale-95 group-active:rotate-0 dark:bg-[#4A90D9]/15 dark:text-[#8CC0F0]">
          <CalendarDays size={13} />
        </span>
        Book a demo
      </Link>
    );
  }

  if (variant === "button") {
    return (
      <Link
        to={contactPath}
        onClick={track}
        className={`group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-base font-medium transition-transform hover:scale-[1.02] active:scale-[0.98] ${accent} ${className}`}
      >
        <CalendarDays size={16} />
        Book a demo
        <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
      </Link>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-[2rem] border ${isForesight ? "border-[#5B21B6]/20 dark:border-[#B9A5FF]/20" : "border-[#1E4FD8]/20 dark:border-[#4A90D9]/25"} bg-white p-7 shadow-[0_24px_70px_-35px_rgba(10,10,15,0.35)] dark:bg-[#0D0D10] sm:p-10 ${className}`}>
      <div className={`absolute -right-20 -top-24 h-64 w-64 rounded-full blur-[90px] ${isForesight ? "bg-[#5B21B6]/15 dark:bg-[#7C4DFF]/20" : "bg-[#1E4FD8]/12 dark:bg-[#4A90D9]/15"}`} />
      <div className="relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
        <div>
          <div className={`inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] ${isForesight ? "text-[#5B21B6] dark:text-[#B9A5FF]" : "text-[#1E4FD8] dark:text-[#8CC0F0]"}`}>
            <CalendarDays size={14} /> A focused 30-minute session
          </div>
          <h2 className="mt-3 font-display text-3xl font-medium tracking-[-0.025em] text-[#0A0A0F] dark:text-white sm:text-4xl">{title}</h2>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-[#5C5C66] dark:text-[#9A9A9A]">{description}</p>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-[#5C5C66] dark:text-[#9A9A9A]">
            {["Live product walkthrough", "Relevant example", "No sales pressure"].map((item) => (
              <span key={item} className="inline-flex items-center gap-1.5"><Check size={14} className={isForesight ? "text-[#5B21B6] dark:text-[#B9A5FF]" : "text-[#1E4FD8] dark:text-[#8CC0F0]"} />{item}</span>
            ))}
          </div>
        </div>
        <BookDemo location={`${location}_button`} variant="button" tone={tone} product={product} />
      </div>
    </div>
  );
}
